/**
 * IRCD Name Builder — Shopify Edition
 * Integrity RC Designs — Neon Circuit Theme
 *
 * Companion builder to the Decal Builder. Lets customers enter a
 * first/last name, pick a font and layout, choose colors & media,
 * and add a name sheet to their Shopify cart.
 *
 * Handles:
 * - Font catalogue loading from Cloudflare R2
 * - Font rendering via FontFace API
 * - SVG text preview with color/outline layers
 * - Grid tiling to fill 7×5" landscape buildsheet
 * - Color palette rendering with media-type restrictions
 * - Shopify /cart/add.js integration with line-item properties
 */

(function () {
  'use strict';

  // ════════════════════════════════════════════════════════
  // CONFIGURATION
  // ════════════════════════════════════════════════════════

  var ASSETS_BASE = 'https://ircd-assets.integrityrcdesigns.workers.dev';

  // Customer email (available when logged in via Shopify)
  var CUSTOMER_EMAIL = (function () {
    var el = document.querySelector('.ircd-builder-page');
    return el ? el.getAttribute('data-customer-email') || null : null;
  })();

  // Shopify product / variant IDs for the Name Builder product.
  // Replace these with actual variant IDs after creating the
  // "Custom RC Name Sheets" product in Shopify admin.
  var VARIANT_IDS = {
    'standard':   '46929492017301',
    'chrome':     '46929492050069',
    'holo':       '46929492082837',
    'flo-orange': '46929492115605',
    'flo-yellow': '46929492148373',
    'flo-green':  '46929492181141',
    'flo-pink':   '46929492213909'
  };

  // Pricing per sheet by media type (in dollars)
  // Easy to adjust — just change the number here
  var PRICING = {
    'standard':   15.00,
    'chrome':     20.00,
    'holo':       20.00,
    'flo-orange': 20.00,
    'flo-yellow': 20.00,
    'flo-green':  20.00,
    'flo-pink':   20.00
  };

  // The 23 standard colour swatches (identical to decal builder)
  var PALETTE = [
    { name: 'White',       hex: '#ffffff' },
    { name: 'Light Gray',  hex: '#d9d9d9' },
    { name: 'Gray',        hex: '#9a9a9a' },
    { name: 'Medium Gray', hex: '#666666' },
    { name: 'Dark Gray',   hex: '#2f2f2f' },
    { name: 'Black',       hex: '#000000' },
    { name: 'Red',         hex: '#ff0000' },
    { name: 'Orange',      hex: '#ff6a00' },
    { name: 'Yellow',      hex: '#ffd500' },
    { name: 'Green',       hex: '#00ff00' },
    { name: 'Light Blue',  hex: '#00c8ff' },
    { name: 'Blue',        hex: '#0055ff' },
    { name: 'Purple',      hex: '#7a00ff' },
    { name: 'Hot Pink',    hex: '#ff00c8' },
    { name: 'Mint',        hex: '#00ff9a' },
    { name: 'Dark Green',  hex: '#00b36b' },
    { name: 'Light Red',   hex: '#ff3d3d' },
    { name: 'Dark Red',    hex: '#b30000' },
    { name: 'Gold',        hex: '#ffb300' },
    { name: 'Brown',       hex: '#7a5200' },
    { name: 'Sky Blue',    hex: '#6bdcff' },
    { name: 'Rose',        hex: '#b07171' },
    { name: 'Light Pink',  hex: '#ffb3d9' }
  ];

  var MEDIA_TYPE_NAMES = {
    'standard':   'Standard',
    'chrome':     'Chrome',
    'holo':       'Holographic',
    'flo-orange': 'Fluorescent Orange',
    'flo-yellow': 'Fluorescent Yellow',
    'flo-green':  'Fluorescent Green',
    'flo-pink':   'Fluorescent Pink'
  };

  var FLUORESCENT_COLORS = {
    'flo-orange': '#ff6a00',
    'flo-yellow': '#ffd500',
    'flo-green':  '#00ff00',
    'flo-pink':   '#ff00c8'
  };

  // Scale dimensions in inches (width of each name)
  var SCALE_WIDTHS = {
    '1/8':  1.5,
    '1/10': 1.25
  };

  // Buildsheet dimensions in inches (landscape)
  var SHEET_WIDTH  = 7;
  var SHEET_HEIGHT = 5;

  // DPI for coordinate conversion (SVG units per inch)
  var DPI = 96;

  // Gap between tiled names in inches
  var TILE_GAP = 0.1; // 0.1" gap between each decal on all sides

  // First name renders at this fraction of the last name's font size
  var FIRST_NAME_RATIO = 0.7;

  // Outline widths in inches (fixed, not relative to font size)
  var OUTLINE1_WIDTH = 0.05;   // 0.05"
  var OUTLINE2_WIDTH = 0.075;  // 0.075"


  // ════════════════════════════════════════════════════════
  // STATE
  // ════════════════════════════════════════════════════════

  var state = {
    firstName: '',
    lastName: '',
    fonts: [],              // array of { id, name, file }
    firstFont: null,        // font id for first name
    lastFont: null,         // font id for last name
    layout: 'stacked',      // always stacked (first name above last name)
    colors: {
      main: null,
      outline1: null,
      outline2: null
    },
    mediaType: 'standard',
    scale: '1/8',
    sheetQuantity: 1,
    templateSvg: null,      // raw SVG text of the 5×7" template
    fontLoadedCache: {},    // fontId -> true
    lastLayout: null        // array of { x, y, w, h }
  };


  // ════════════════════════════════════════════════════════
  // DOM HELPERS
  // ════════════════════════════════════════════════════════

  function qa(attr) { return document.querySelectorAll('[data-' + attr + ']'); }
  function q(attr)  { return document.querySelector('[data-' + attr + ']'); }
  function el(id)   { return document.getElementById(id); }

  function normalize(s) { return (s || '').toLowerCase().trim(); }

  function pad2(n) { return String(n).padStart(2, '0'); }

  function formatMMDDYYYY(d) {
    return pad2(d.getMonth() + 1) + '-' + pad2(d.getDate()) + '-' + d.getFullYear();
  }


  // ════════════════════════════════════════════════════════
  // FONT LIST (Google Fonts + Custom R2 Fonts)
  // ════════════════════════════════════════════════════════

  // Google Fonts — preloaded via CSS in the Liquid template.
  // Ordered: popular racing/bold fonts first, then brush/script, then more display.
  var GOOGLE_FONTS = [
    // ── Popular / Racing / Bold ──
    { id: 'Bebas Neue',      name: 'Bebas Neue',      source: 'google' },
    { id: 'Oswald',          name: 'Oswald',          source: 'google' },
    { id: 'Racing Sans One', name: 'Racing Sans One', source: 'google' },
    { id: 'Russo One',       name: 'Russo One',       source: 'google' },
    { id: 'Black Ops One',   name: 'Black Ops One',   source: 'google' },
    { id: 'Anton',           name: 'Anton',           source: 'google' },
    { id: 'Teko',            name: 'Teko',            source: 'google' },
    { id: 'Bungee',          name: 'Bungee',          source: 'google' },
    { id: 'Orbitron',        name: 'Orbitron',        source: 'google' },
    { id: 'Audiowide',       name: 'Audiowide',       source: 'google' },
    { id: 'Fugaz One',       name: 'Fugaz One',       source: 'google' },
    { id: 'Archivo Black',   name: 'Archivo Black',   source: 'google' },
    // ── Brush / Script / Cursive ──
    { id: 'Permanent Marker', name: 'Permanent Marker', source: 'google' },
    { id: 'Kaushan Script',  name: 'Kaushan Script',  source: 'google' },
    { id: 'Pacifico',        name: 'Pacifico',        source: 'google' },
    { id: 'Lobster',         name: 'Lobster',         source: 'google' },
    { id: 'Dancing Script',  name: 'Dancing Script',  source: 'google' },
    // ── More Display / Fun ──
    { id: 'Bangers',         name: 'Bangers',         source: 'google' },
    { id: 'Staatliches',     name: 'Staatliches',     source: 'google' },
    { id: 'Righteous',       name: 'Righteous',       source: 'google' },
    { id: 'Passion One',     name: 'Passion One',     source: 'google' },
    { id: 'Alfa Slab One',   name: 'Alfa Slab One',   source: 'google' },
    { id: 'Changa One',      name: 'Changa One',      source: 'google' },
    { id: 'Fredoka One',     name: 'Fredoka One',     source: 'google' },
    { id: 'Luckiest Guy',    name: 'Luckiest Guy',    source: 'google' },
    { id: 'Titan One',       name: 'Titan One',       source: 'google' },
    { id: 'Lilita One',      name: 'Lilita One',      source: 'google' },
    { id: 'Graduate',        name: 'Graduate',        source: 'google' },
    { id: 'Saira Stencil One', name: 'Saira Stencil One', source: 'google' },
    { id: 'Press Start 2P',  name: 'Press Start 2P',  source: 'google' }
  ];

  // Load fonts: fetch custom fonts from R2, merge with Google Fonts
  function loadFonts() {
    return fetch(ASSETS_BASE + '/r2/fonts/fonts.json', { cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('fonts.json not found');
        return r.json();
      })
      .then(function (data) {
        // Custom fonts from R2 go at the TOP of the list
        var customFonts = Array.isArray(data.fonts) ? data.fonts : [];
        var mapped = [];
        for (var i = 0; i < customFonts.length; i++) {
          var f = customFonts[i];
          if (!f.id || !f.file) continue;
          mapped.push({
            id: f.id,
            name: f.name || f.id,
            file: f.file,
            source: 'r2'
          });
        }
        // Custom fonts first, then Google Fonts
        state.fonts = mapped.concat(GOOGLE_FONTS);
        console.log('IRCD fonts loaded:', mapped.length, 'custom +', GOOGLE_FONTS.length, 'Google');
      })
      .catch(function (err) {
        // No custom fonts, just use Google Fonts
        console.log('No custom fonts found, using Google Fonts only');
        state.fonts = GOOGLE_FONTS;
      })
      .then(function () {
        renderFontSelects();

        // Auto-select the first font for both selectors
        if (state.fonts.length > 0) {
          state.firstFont = state.fonts[0].id;
          state.lastFont = state.fonts[0].id;
          var firstSelect = q('first-font');
          var lastSelect = q('last-font');
          if (firstSelect) firstSelect.value = state.fonts[0].id;
          if (lastSelect) lastSelect.value = state.fonts[0].id;

          // Preload first custom font if it's from R2
          if (state.fonts[0].source === 'r2') {
            loadCustomFont(state.fonts[0].id);
          }

          applyFontToInput('first');
          applyFontToInput('last');
        }
      });
  }

  // Load a custom font from R2 via FontFace API (called on-demand)
  function loadCustomFont(fontId) {
    if (state.fontLoadedCache[fontId]) return Promise.resolve();

    var fontObj = null;
    for (var i = 0; i < state.fonts.length; i++) {
      if (state.fonts[i].id === fontId && state.fonts[i].source === 'r2') {
        fontObj = state.fonts[i];
        break;
      }
    }
    if (!fontObj) return Promise.resolve();

    var url = ASSETS_BASE + '/r2/fonts/' + encodeURIComponent(fontObj.file);
    var face = new FontFace(fontObj.id, 'url(' + url + ')');

    return face.load().then(function (loadedFace) {
      document.fonts.add(loadedFace);
      state.fontLoadedCache[fontId] = true;
      console.log('Custom font loaded:', fontObj.name);
    }).catch(function (err) {
      console.error('Failed to load custom font:', fontObj.name, err);
    });
  }

  // Apply selected font to the corresponding input field
  function applyFontToInput(which) {
    var inputEl = q(which + '-name');
    var fontId = which === 'first' ? state.firstFont : state.lastFont;
    if (inputEl && fontId) {
      inputEl.style.fontFamily = '"' + fontId + '", sans-serif';
    }
  }

  // Populate both font <select> dropdowns
  function renderFontSelects() {
    var selects = [
      { el: q('first-font'), type: 'first' },
      { el: q('last-font'), type: 'last' }
    ];

    for (var si = 0; si < selects.length; si++) {
      var selectEl = selects[si].el;
      if (!selectEl) continue;

      selectEl.innerHTML = '';

      if (state.fonts.length === 0) {
        var opt = document.createElement('option');
        opt.value = '';
        opt.textContent = 'No fonts available';
        selectEl.appendChild(opt);
        continue;
      }

      var lastSource = null;
      for (var i = 0; i < state.fonts.length; i++) {
        var font = state.fonts[i];

        // Add separator between custom and Google fonts
        if (lastSource === 'r2' && font.source === 'google') {
          var sep = document.createElement('option');
          sep.disabled = true;
          sep.textContent = '── Google Fonts ──';
          selectEl.appendChild(sep);
        }

        var opt = document.createElement('option');
        opt.value = font.id;
        opt.textContent = font.name;
        opt.style.fontFamily = '"' + font.id + '", sans-serif';
        selectEl.appendChild(opt);

        lastSource = font.source;
      }

      // Style the select element itself with the current font
      var currentFont = selects[si].type === 'first' ? state.firstFont : state.lastFont;
      if (currentFont) {
        selectEl.style.fontFamily = '"' + currentFont + '", sans-serif';
      }
    }
  }


  // ════════════════════════════════════════════════════════
  // TEMPLATE LOADING
  // ════════════════════════════════════════════════════════

  function loadTemplate() {
    return fetch(ASSETS_BASE + '/r2/templates/name-buildsheet.svg', { cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('Template fetch failed: ' + r.status);
        return r.text();
      })
      .then(function (svgText) {
        state.templateSvg = svgText;
        console.log('Name buildsheet template loaded');
      })
      .catch(function (err) {
        console.error('Template loading error:', err);
      });
  }


  // ════════════════════════════════════════════════════════
  // SVG PREVIEW RENDERING
  // ════════════════════════════════════════════════════════

  // Debounce helper to avoid rendering on every keystroke
  var renderTimeout = null;
  function scheduleRender() {
    if (renderTimeout) clearTimeout(renderTimeout);
    renderTimeout = setTimeout(function () {
      renderPreview();
    }, 150);
  }

  function renderPreview() {
    var host = q('preview-host');
    if (!host) return;

    var firstName = state.firstName.trim();
    var lastName = state.lastName.trim();

    // If template not loaded yet, show placeholder
    if (!state.templateSvg) {
      host.innerHTML = createPlaceholderSvg();
      updateStats(0);
      return;
    }

    // If no name entered, show template with just metadata (no names)
    if (!firstName && !lastName) {
      renderEmptyTemplate(host);
      updateStats(0);
      return;
    }

    // Ensure custom fonts are loaded before rendering
    var promises = [];
    if (state.firstFont) promises.push(loadCustomFont(state.firstFont));
    if (state.lastFont) promises.push(loadCustomFont(state.lastFont));

    Promise.all(promises).then(function () {
      // Wait for ALL fonts (including Google Fonts) to be ready before measuring
      // This ensures getBBox() returns accurate dimensions after font changes
      document.fonts.ready.then(function () {
        doRender(host, firstName, lastName);
      });
    });
  }

  // Render the template without names (just shows buildsheet with metadata)
  function renderEmptyTemplate(host) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(state.templateSvg, 'image/svg+xml');

    // Populate metadata fields
    populateMetadata(doc, '', '');

    var serializer = new XMLSerializer();
    host.innerHTML = serializer.serializeToString(doc);
  }

  // Populate customer name and date fields in the template.
  // The customerName and dateOrdered groups contain placeholder text that needs to be replaced.
  // Labels (with colons) are separate elements to the left of these groups.
  function populateMetadata(doc, firstName, lastName) {
    var ns = 'http://www.w3.org/2000/svg';

    // Helper: clear a group and inject new text value
    function injectValue(groupId, text, xPos, yPos) {
      var group = doc.getElementById(groupId);
      if (!group) return;

      // Clear all children (remove placeholder text)
      while (group.firstChild) {
        group.removeChild(group.firstChild);
      }

      // Create fresh text element at the placeholder's original position
      var textEl = doc.createElementNS(ns, 'text');
      textEl.setAttribute('transform', 'translate(' + xPos + ' ' + yPos + ') scale(1.348 1)');
      textEl.setAttribute('fill', '#231f20');
      textEl.setAttribute('font-family', "MyriadPro-BoldIt, 'Myriad Pro'");
      textEl.setAttribute('font-size', '7.854');
      textEl.setAttribute('font-style', 'italic');
      textEl.setAttribute('font-weight', '700');

      var tspan = doc.createElementNS(ns, 'tspan');
      tspan.setAttribute('x', '0');
      tspan.setAttribute('y', '0');
      tspan.textContent = text;
      textEl.appendChild(tspan);

      group.appendChild(textEl);
    }

    // Replace customerName placeholder (original starts at x=331.277, y=348.516)
    var fullName = [firstName, lastName].filter(Boolean).join(' ');
    injectValue('customerName', fullName, 331.277, 348.516);

    // Replace dateOrdered placeholder (original starts at x=331.277, y=362.889)
    injectValue('dateOrdered', formatMMDDYYYY(new Date()), 331.277, 362.889);
  }

  function doRender(host, firstName, lastName) {
    // Parse the template SVG
    var parser = new DOMParser();
    var doc = parser.parseFromString(state.templateSvg, 'image/svg+xml');
    var svg = doc.documentElement;

    // Get SVG viewBox dimensions to work in SVG coordinate space
    var vb = svg.getAttribute('viewBox');
    var vbParts = vb ? vb.split(/[\s,]+/).map(Number) : [0, 0, SHEET_WIDTH * DPI, SHEET_HEIGHT * DPI];
    var svgW = vbParts[2];
    var svgH = vbParts[3];

    // Calculate units per inch from the viewBox
    var upi = svgW / SHEET_WIDTH;

    // Shrink text so text + outlines = scale width (e.g. 1.5" for 1/8)
    var outlineExpansion = (OUTLINE1_WIDTH + OUTLINE2_WIDTH) * 2; // both sides
    var targetWidth = (SCALE_WIDTHS[state.scale] - outlineExpansion) * upi;
    var gapUnits = TILE_GAP * upi;

    // Create a temporary hidden SVG to measure text
    var measSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    measSvg.setAttribute('width', '0');
    measSvg.setAttribute('height', '0');
    measSvg.style.position = 'absolute';
    measSvg.style.visibility = 'hidden';
    measSvg.style.overflow = 'hidden';
    document.body.appendChild(measSvg);

    var firstFontFamily = state.firstFont || 'sans-serif';
    var lastFontFamily = state.lastFont || 'sans-serif';
    var fontSize = 72; // start at a large size for measurement

    // Build the text content based on layout
    var textGroup = createNameTextGroup(doc, firstName, lastName, firstFontFamily, lastFontFamily, fontSize);
    measSvg.appendChild(textGroup);

    // Measure the bounding box
    var bbox;
    try {
      bbox = textGroup.getBBox();
    } catch (e) {
      // getBBox can fail if text hasn't rendered yet
      bbox = { x: 0, y: 0, width: 200, height: 50 };
    }

    document.body.removeChild(measSvg);

    // Calculate scale factor to fit the target width
    var scaleFactor = targetWidth / bbox.width;
    var nameW = targetWidth;
    var nameH = bbox.height * scaleFactor;

    // Determine safe area — look for a rect with id="safeArea",
    // otherwise use a margin inside the full sheet
    var safeArea = getSafeArea(doc, svgW, svgH, upi);

    // Gap between decals accounts for full outline expansion (outline1 + outline2)
    // on both adjacent decals, plus the TILE_GAP clearance between outline edges.
    var outlinePad = (OUTLINE1_WIDTH + OUTLINE2_WIDTH) * upi * 2; // both decals' full outline
    var effectiveGap = gapUnits + outlinePad;

    // Reserve space for outline expansion on the outer edges of the grid.
    // Inner gaps already account for adjacent outlines via effectiveGap,
    // but the first/last column and row outlines extend beyond nameW/nameH.
    var outerOutline = (OUTLINE1_WIDTH + OUTLINE2_WIDTH) * upi;
    var availW = safeArea.w - 2 * outerOutline;
    var availH = safeArea.h - 2 * outerOutline;

    // Calculate grid tiling — how many copies fit on the sheet
    var cols = Math.floor((availW + effectiveGap) / (nameW + effectiveGap));
    var rows = Math.floor((availH + effectiveGap) / (nameH + effectiveGap));
    if (cols < 1) cols = 1;
    if (rows < 1) rows = 1;

    var totalNames = cols * rows;
    var placements = [];

    // Find or create the name content layer
    var layer = doc.getElementById('nameContent');
    if (!layer) {
      layer = doc.createElementNS('http://www.w3.org/2000/svg', 'g');
      layer.setAttribute('id', 'nameContent');
      svg.appendChild(layer);
    } else {
      // Clear existing content
      while (layer.firstChild) layer.removeChild(layer.firstChild);
    }

    // Center the grid within the reduced area (after outer outline reservation)
    var totalGridW = cols * nameW + (cols - 1) * effectiveGap;
    var totalGridH = rows * nameH + (rows - 1) * effectiveGap;
    var offsetX = safeArea.x + outerOutline + (availW - totalGridW) / 2;
    var offsetY = safeArea.y + outerOutline + (availH - totalGridH) / 2;

    // Stroke widths in pre-scale SVG units. stroke-width is centered on the
    // path, but paint-order="stroke fill" lets fill cover the inner half,
    // so we need 2x the desired outward expansion.
    var o1StrokeW = (OUTLINE1_WIDTH * upi / scaleFactor) * 2;
    var o2StrokeW = ((OUTLINE1_WIDTH + OUTLINE2_WIDTH) * upi / scaleFactor) * 2;

    var defs = doc.querySelector('defs') || doc.createElementNS('http://www.w3.org/2000/svg', 'defs');
    if (!defs.parentNode) svg.insertBefore(defs, svg.firstChild);

    // Clean up old gradient defs from previous renders
    var oldIds = ['ircd-grad-main', 'ircd-grad-o1', 'ircd-grad-o2',
                  'ircd-o1-gmask', 'ircd-o2-gmask'];
    for (var oi = 0; oi < oldIds.length; oi++) {
      var oldEl = doc.getElementById(oldIds[oi]);
      if (oldEl) oldEl.parentNode.removeChild(oldEl);
    }

    var mainColorRaw = state.colors.main || '#cccccc';
    var o1ColorRaw = state.colors.outline1;
    var o2ColorRaw = state.colors.outline2;
    var ns = 'http://www.w3.org/2000/svg';

    // Resolve special material values (chrome/holo) to SVG gradient fills.
    // Creates linearGradient elements in <defs> and returns { fill, hex }.
    function resolveColor(raw, prefix) {
      if (raw === 'chrome') {
        var gId = 'ircd-grad-' + prefix;
        var grad = doc.createElementNS(ns, 'linearGradient');
        grad.setAttribute('id', gId);
        grad.setAttribute('x1', '0%'); grad.setAttribute('y1', '0%');
        grad.setAttribute('x2', '100%'); grad.setAttribute('y2', '0%');
        var chromeStops = [
          ['0%','#e5e2df'],['6%','#cfcac8'],['18%','#a8a4a6'],
          ['26%','#8b878d'],['52%','#f3f4f4'],['58%','#e4e5e5'],
          ['70%','#bfbec0'],['88%','#807b80'],['100%','#a7a9ac']
        ];
        for (var si = 0; si < chromeStops.length; si++) {
          var s = doc.createElementNS(ns, 'stop');
          s.setAttribute('offset', chromeStops[si][0]);
          s.setAttribute('stop-color', chromeStops[si][1]);
          grad.appendChild(s);
        }
        defs.appendChild(grad);
        return { fill: 'url(#' + gId + ')', hex: '#bfbec0' };
      }
      if (raw === 'holo') {
        var gId = 'ircd-grad-' + prefix;
        var grad = doc.createElementNS(ns, 'linearGradient');
        grad.setAttribute('id', gId);
        grad.setAttribute('x1', '0%'); grad.setAttribute('y1', '0%');
        grad.setAttribute('x2', '100%'); grad.setAttribute('y2', '0%');
        var holoStops = [
          ['0%','#ffe6b1'],['11%','#fffde7'],['22%','#ddb65d'],
          ['25%','#d09b27'],['40%','#f3e85c'],['50%','#cfc65e'],
          ['58%','#a49e60'],['70%','#6f7d64'],['77%','#3b756d'],
          ['84%','#035777'],['87%','#005678'],['94%','#549fd7'],
          ['100%','#bcd9f2']
        ];
        for (var si = 0; si < holoStops.length; si++) {
          var s = doc.createElementNS(ns, 'stop');
          s.setAttribute('offset', holoStops[si][0]);
          s.setAttribute('stop-color', holoStops[si][1]);
          grad.appendChild(s);
        }
        defs.appendChild(grad);
        return { fill: 'url(#' + gId + ')', hex: '#d09b27' };
      }
      return { fill: raw, hex: raw };
    }

    var mainResolved = resolveColor(mainColorRaw, 'main');
    var o1Resolved = o1ColorRaw ? resolveColor(o1ColorRaw, 'o1') : null;
    var o2Resolved = o2ColorRaw ? resolveColor(o2ColorRaw, 'o2') : null;

    var o1IsSpecial = (o1ColorRaw === 'chrome' || o1ColorRaw === 'holo');
    var o2IsSpecial = (o2ColorRaw === 'chrome' || o2ColorRaw === 'holo');

    // Apply rounded stroke attributes to a text group for outline rendering.
    // paint-order="stroke fill" renders the stroke behind the fill, so the
    // fill covers the inner half and only the outward expansion shows.
    function applyStrokeOutline(g, strokeColor, strokeWidth) {
      g.setAttribute('stroke', strokeColor);
      g.setAttribute('stroke-width', String(strokeWidth));
      g.setAttribute('stroke-linejoin', 'round');
      g.setAttribute('stroke-linecap', 'round');
      g.setAttribute('paint-order', 'stroke fill');
    }

    // Collect parts by layer so we can render in correct z-order:
    // outline2 (back) -> outline1 (middle) -> main (front).
    // Special-material outlines collect mask content separately.
    var o2Parts = [], o1Parts = [], mainParts = [];
    var o2MaskParts = [], o1MaskParts = [];

    for (var row = 0; row < rows; row++) {
      for (var col = 0; col < cols; col++) {
        var px = offsetX + col * (nameW + effectiveGap);
        var py = offsetY + row * (nameH + effectiveGap);
        var tx = px - bbox.x * scaleFactor;
        var ty = py - bbox.y * scaleFactor;
        var transformStr = 'translate(' + tx + ',' + ty + ') scale(' + scaleFactor + ')';

        // Outline 2 (back layer, thickest)
        if (o2ColorRaw) {
          var o2g = buildTextElements(doc, firstName, lastName, firstFontFamily, lastFontFamily, fontSize);
          o2g.setAttribute('transform', transformStr);
          if (o2IsSpecial) {
            o2g.setAttribute('fill', 'white');
            applyStrokeOutline(o2g, 'white', o2StrokeW);
            o2MaskParts.push(o2g);
          } else {
            o2g.setAttribute('fill', o2Resolved.fill);
            applyStrokeOutline(o2g, o2Resolved.hex, o2StrokeW);
            o2Parts.push(o2g);
          }
        }

        // Outline 1 (middle layer)
        if (o1ColorRaw) {
          var o1g = buildTextElements(doc, firstName, lastName, firstFontFamily, lastFontFamily, fontSize);
          o1g.setAttribute('transform', transformStr);
          if (o1IsSpecial) {
            o1g.setAttribute('fill', 'white');
            applyStrokeOutline(o1g, 'white', o1StrokeW);
            o1MaskParts.push(o1g);
          } else {
            o1g.setAttribute('fill', o1Resolved.fill);
            applyStrokeOutline(o1g, o1Resolved.hex, o1StrokeW);
            o1Parts.push(o1g);
          }
        }

        // Main fill (front layer, no stroke)
        var mainG = buildTextElements(doc, firstName, lastName, firstFontFamily, lastFontFamily, fontSize);
        mainG.setAttribute('transform', transformStr);
        mainG.setAttribute('fill', mainResolved.fill);
        mainParts.push(mainG);

        placements.push({ x: px, y: py, w: nameW, h: nameH });
      }
    }

    // Render layers in z-order: outline2 -> outline1 -> main.
    // Special-material outlines use a <mask> + gradient <rect>.
    if (o2IsSpecial && o2MaskParts.length) {
      var mask2 = doc.createElementNS(ns, 'mask');
      mask2.setAttribute('id', 'ircd-o2-gmask');
      for (var mi = 0; mi < o2MaskParts.length; mi++) mask2.appendChild(o2MaskParts[mi]);
      defs.appendChild(mask2);
      var rect2 = doc.createElementNS(ns, 'rect');
      rect2.setAttribute('x', '0'); rect2.setAttribute('y', '0');
      rect2.setAttribute('width', String(svgW)); rect2.setAttribute('height', String(svgH));
      rect2.setAttribute('fill', o2Resolved.fill);
      rect2.setAttribute('mask', 'url(#ircd-o2-gmask)');
      layer.appendChild(rect2);
    } else {
      for (var pi = 0; pi < o2Parts.length; pi++) layer.appendChild(o2Parts[pi]);
    }

    if (o1IsSpecial && o1MaskParts.length) {
      var mask1 = doc.createElementNS(ns, 'mask');
      mask1.setAttribute('id', 'ircd-o1-gmask');
      for (var mi = 0; mi < o1MaskParts.length; mi++) mask1.appendChild(o1MaskParts[mi]);
      defs.appendChild(mask1);
      var rect1 = doc.createElementNS(ns, 'rect');
      rect1.setAttribute('x', '0'); rect1.setAttribute('y', '0');
      rect1.setAttribute('width', String(svgW)); rect1.setAttribute('height', String(svgH));
      rect1.setAttribute('fill', o1Resolved.fill);
      rect1.setAttribute('mask', 'url(#ircd-o1-gmask)');
      layer.appendChild(rect1);
    } else {
      for (var pi = 0; pi < o1Parts.length; pi++) layer.appendChild(o1Parts[pi]);
    }

    for (var pi = 0; pi < mainParts.length; pi++) layer.appendChild(mainParts[pi]);

    state.lastLayout = placements;

    // Populate metadata fields (customer name, date)
    populateMetadata(doc, firstName, lastName);

    // Serialize and render
    var serializer = new XMLSerializer();
    host.innerHTML = serializer.serializeToString(doc);

    updateStats(totalNames);
  }

  // Create SVG text elements for the name (for measurement)
  // Stacked: first name centered on top, last name below with tight spacing
  function createNameTextGroup(doc, firstName, lastName, firstFont, lastFont, fontSize) {
    var ns = 'http://www.w3.org/2000/svg';
    var g = document.createElementNS(ns, 'g');
    // First name uses a smaller font size so the last name is more prominent
    var firstFontSize = Math.round(fontSize * FIRST_NAME_RATIO);

    var text1 = document.createElementNS(ns, 'text');
    text1.setAttribute('font-family', firstFont);
    text1.setAttribute('font-size', String(firstFontSize));
    text1.setAttribute('fill', '#000000');
    text1.setAttribute('text-anchor', 'middle');
    text1.setAttribute('x', '0');
    text1.setAttribute('y', '0');
    text1.textContent = firstName || '';
    g.appendChild(text1);

    var text2 = document.createElementNS(ns, 'text');
    text2.setAttribute('font-family', lastFont);
    text2.setAttribute('font-size', String(fontSize));
    text2.setAttribute('fill', '#000000');
    text2.setAttribute('text-anchor', 'middle');
    text2.setAttribute('x', '0');
    text2.setAttribute('y', String(firstFontSize * 1.15));
    text2.textContent = lastName || '';
    g.appendChild(text2);

    return g;
  }

  // Create a name group with the 3-layer color system using feMorphology dilate.
  // Each layer is a separate <g> with its own transform and optional filter.
  // The filter references (ircd-o1, ircd-o2) are created in doRender's <defs>.
  function createColoredNameGroup(doc, firstName, lastName, firstFont, lastFont, fontSize, scaleFactor, bbox, px, py) {
    var ns = 'http://www.w3.org/2000/svg';
    var g = doc.createElementNS(ns, 'g');

    // Transform: translate to position, scale to fit target width
    var tx = px - bbox.x * scaleFactor;
    var ty = py - bbox.y * scaleFactor;
    var transformStr = 'translate(' + tx + ',' + ty + ') scale(' + scaleFactor + ')';

    var mainColor = state.colors.main || '#000000';
    var o1Color = state.colors.outline1;
    var o2Color = state.colors.outline2;

    // Layer order: outline2 (back), outline1 (middle), main (front)
    // Each layer uses feMorphology dilate instead of stroke — this expands
    // the text shape outward uniformly without inner counter gaps.

    if (o2Color) {
      var o2Layer = buildTextElements(doc, firstName, lastName, firstFont, lastFont, fontSize);
      o2Layer.setAttribute('transform', transformStr);
      o2Layer.setAttribute('filter', 'url(#ircd-o2)');
      o2Layer.setAttribute('fill', o2Color);
      g.appendChild(o2Layer);
    }

    if (o1Color) {
      var o1Layer = buildTextElements(doc, firstName, lastName, firstFont, lastFont, fontSize);
      o1Layer.setAttribute('transform', transformStr);
      o1Layer.setAttribute('filter', 'url(#ircd-o1)');
      o1Layer.setAttribute('fill', o1Color);
      g.appendChild(o1Layer);
    }

    // Main fill layer (no filter, no expansion)
    var mainLayer = buildTextElements(doc, firstName, lastName, firstFont, lastFont, fontSize);
    mainLayer.setAttribute('transform', transformStr);
    mainLayer.setAttribute('fill', mainColor);
    g.appendChild(mainLayer);

    return g;
  }

  // Build SVG text elements for a single name instance
  // Stacked: first name centered, last name below with tight spacing
  // No fill set here — parent <g> sets fill via inheritance
  function buildTextElements(doc, firstName, lastName, firstFont, lastFont, fontSize) {
    var ns = 'http://www.w3.org/2000/svg';
    var g = doc.createElementNS(ns, 'g');
    var firstFontSize = Math.round(fontSize * FIRST_NAME_RATIO);

    var text1 = doc.createElementNS(ns, 'text');
    text1.setAttribute('font-family', firstFont);
    text1.setAttribute('font-size', String(firstFontSize));
    text1.setAttribute('text-anchor', 'middle');
    text1.setAttribute('x', '0');
    text1.setAttribute('y', '0');
    text1.textContent = firstName || '';
    g.appendChild(text1);

    var text2 = doc.createElementNS(ns, 'text');
    text2.setAttribute('font-family', lastFont);
    text2.setAttribute('font-size', String(fontSize));
    text2.setAttribute('text-anchor', 'middle');
    text2.setAttribute('x', '0');
    text2.setAttribute('y', String(firstFontSize * 1.15));
    text2.textContent = lastName || '';
    g.appendChild(text2);

    return g;
  }

  // Note: applyColorToTextGroup removed — fill is now set on parent <g>
  // elements and inherited by text. Outlines use feMorphology dilate filters
  // instead of stroke, so no stroke attributes are needed.

  // Get the safe area from the template or calculate a default
  function getSafeArea(doc, svgW, svgH, upi) {
    var safeRect = doc.getElementById('safeArea');
    if (safeRect) {
      return {
        x: parseFloat(safeRect.getAttribute('x')) || 0,
        y: parseFloat(safeRect.getAttribute('y')) || 0,
        w: parseFloat(safeRect.getAttribute('width')) || svgW,
        h: parseFloat(safeRect.getAttribute('height')) || svgH
      };
    }
    // Default: 0.25" margin on all sides
    var margin = 0.25 * upi;
    return {
      x: margin,
      y: margin,
      w: svgW - margin * 2,
      h: svgH - margin * 2
    };
  }

  // Create a placeholder SVG when no name is entered
  function createPlaceholderSvg() {
    return '<svg viewBox="0 0 ' + (SHEET_WIDTH * DPI) + ' ' + (SHEET_HEIGHT * DPI) + '" xmlns="http://www.w3.org/2000/svg">' +
      '<rect width="100%" height="100%" fill="#1a1a2e" rx="8"/>' +
      '<text x="50%" y="50%" text-anchor="middle" fill="#555" font-size="18" font-family="sans-serif">' +
      'Enter a name to preview' +
      '</text>' +
      '</svg>';
  }

  // Update the stats pill with name count
  function updateStats(count) {
    var textEl = q('stats-text');
    var dotEl = q('stats-dot');
    if (textEl) {
      textEl.textContent = count + ' name' + (count !== 1 ? 's' : '');
    }
    if (dotEl) {
      dotEl.className = 'ircd-health-dot' + (count > 0 ? ' is-ok' : '');
    }
  }


  // ════════════════════════════════════════════════════════
  // COLOUR PALETTES
  // (Copied from decal builder — same logic)
  // ════════════════════════════════════════════════════════

  function getFluorescentColor(mediaType) {
    return FLUORESCENT_COLORS[mediaType] || '#ff6a00';
  }

  function canSelectChromeHolo(key) {
    if (key === 'main') return true;
    // Outline can only be chrome/holo if the layer above isn't already chrome/holo
    if (key === 'outline1') return state.colors.main !== 'chrome' && state.colors.main !== 'holo';
    if (key === 'outline2') return state.colors.outline1 !== 'chrome' && state.colors.outline1 !== 'holo';
    return true;
  }

  function renderPalette(targetAttr, key) {
    var wrap = document.querySelector('[data-palette="' + targetAttr + '"]');
    if (!wrap) return;
    wrap.innerHTML = '';

    var mt = state.mediaType;

    // ── Chrome / Holo ──
    if (mt === 'chrome' || mt === 'holo') {
      for (var ci = 0; ci < PALETTE.length; ci++) {
        if (PALETTE[ci].hex === '#ffffff') continue;
        (function (color) {
          var sw = document.createElement('div');
          sw.className = 'ircd-swatch' + (state.colors[key] === color.hex ? ' is-selected' : '');
          sw.style.setProperty('--c', color.hex);
          sw.title = color.name;
          sw.onclick = function () {
            state.colors[key] = color.hex;
            renderAllPalettes();
            scheduleRender();
          };
          wrap.appendChild(sw);
        })(PALETTE[ci]);
      }

      // Chrome/Holo button — stores 'chrome' or 'holo' as special material value
      var specialVal = mt; // 'chrome' or 'holo'
      var btn = document.createElement('div');
      btn.className = (mt === 'chrome' ? 'ircd-swatch--chrome' : 'ircd-swatch--holo') +
        (state.colors[key] === specialVal || !state.colors[key] ? ' is-selected' : '');
      btn.textContent = mt === 'chrome' ? 'Chrome' : 'Holo';
      btn.style.cursor = canSelectChromeHolo(key) ? 'pointer' : 'not-allowed';
      btn.style.opacity = canSelectChromeHolo(key) ? '1' : '0.3';
      if (canSelectChromeHolo(key)) {
        btn.onclick = function () { state.colors[key] = specialVal; renderAllPalettes(); scheduleRender(); };
      }
      wrap.appendChild(btn);
      return;
    }

    // ── Fluorescent ──
    if (mt.indexOf('flo-') === 0) {
      var floColor = getFluorescentColor(mt);
      var floName = mt.replace('flo-', '').replace(/^./, function (c) { return c.toUpperCase(); });

      if (key === 'main') {
        var mainOpts = [{ hex: '#000000', name: 'Black' }, { hex: floColor, name: floName }];
        for (var mi = 0; mi < mainOpts.length; mi++) {
          (function (opt) {
            var sw = document.createElement('div');
            sw.className = 'ircd-swatch' + (state.colors[key] === opt.hex ? ' is-selected' : '');
            sw.style.setProperty('--c', opt.hex);
            sw.title = opt.name;
            sw.onclick = function () {
              state.colors.main = opt.hex;
              state.colors.outline1 = (opt.hex === '#000000') ? floColor : '#000000';
              renderAllPalettes();
              scheduleRender();
            };
            wrap.appendChild(sw);
          })(mainOpts[mi]);
        }
        return;
      }

      if (key === 'outline1') {
        var mainColor = state.colors.main || '#000000';
        var o1Color = (mainColor === '#000000') ? floColor : '#000000';
        state.colors.outline1 = o1Color;
        var sw = document.createElement('div');
        sw.className = 'ircd-swatch is-selected';
        sw.style.setProperty('--c', o1Color);
        sw.style.opacity = '0.6';
        sw.style.cursor = 'not-allowed';
        sw.title = (o1Color === '#000000' ? 'Black' : floName) + ' (auto-set)';
        wrap.appendChild(sw);
        return;
      }

      if (key === 'outline2') {
        var o2Opts = [{ hex: '#000000', name: 'Black' }, { hex: floColor, name: floName }];
        for (var o2i = 0; o2i < o2Opts.length; o2i++) {
          (function (opt) {
            var sw = document.createElement('div');
            sw.className = 'ircd-swatch' + (state.colors[key] === opt.hex ? ' is-selected' : '');
            sw.style.setProperty('--c', opt.hex);
            sw.title = opt.name;
            sw.onclick = function () {
              state.colors.outline2 = opt.hex;
              renderAllPalettes();
              scheduleRender();
            };
            wrap.appendChild(sw);
          })(o2Opts[o2i]);
        }
        return;
      }
    }

    // ── Standard — full palette ──
    for (var si = 0; si < PALETTE.length; si++) {
      (function (color) {
        var sw = document.createElement('div');
        sw.className = 'ircd-swatch' + (state.colors[key] === color.hex ? ' is-selected' : '');
        sw.style.setProperty('--c', color.hex);
        sw.title = color.name;
        sw.onclick = function () {
          state.colors[key] = color.hex;
          renderAllPalettes();
          scheduleRender();
        };
        wrap.appendChild(sw);
      })(PALETTE[si]);
    }
  }

  function renderAllPalettes() {
    renderPalette('main', 'main');
    renderPalette('outline1', 'outline1');
    renderPalette('outline2', 'outline2');
  }


  // ════════════════════════════════════════════════════════
  // MEDIA TYPE COLOUR LOGIC
  // ════════════════════════════════════════════════════════

  function applyMediaTypeColorLogic(mediaType) {
    if (mediaType === 'standard') return;
    // Default to the material color when switching to chrome/holo
    if (mediaType === 'chrome' || mediaType === 'holo') {
      if (!state.colors.main || state.colors.main === 'chrome' || state.colors.main === 'holo') {
        state.colors.main = mediaType;
      }
      return;
    }
    if (mediaType.indexOf('flo-') === 0) {
      var floColor = getFluorescentColor(mediaType);
      if (!state.colors.main || state.colors.main === 'chrome' || state.colors.main === 'holo') state.colors.main = '#000000';
      if (state.colors.main !== '#000000' && state.colors.main !== floColor) state.colors.main = '#000000';
      state.colors.outline1 = (state.colors.main === '#000000') ? floColor : '#000000';
      if (!state.colors.outline2 || (state.colors.outline2 !== '#000000' && state.colors.outline2 !== floColor)) {
        state.colors.outline2 = '#000000';
      }
    }
  }


  // ════════════════════════════════════════════════════════
  // PRICING
  // ════════════════════════════════════════════════════════

  function updatePricing() {
    var price = PRICING[state.mediaType] || PRICING.standard;
    var total = price * state.sheetQuantity;

    var perSheetEl = q('price-per-sheet');
    var totalEl = q('price-total');
    if (perSheetEl) perSheetEl.textContent = '$' + price.toFixed(2);
    if (totalEl) totalEl.textContent = '$' + total.toFixed(2);
  }


  // ════════════════════════════════════════════════════════
  // ORDER VALIDATION & CART
  // ════════════════════════════════════════════════════════

  function validateOrder() {
    var errors = [];

    if (!state.firstName.trim()) errors.push('First name required');
    if (!state.lastName.trim()) errors.push('Last name required');
    if (!state.firstFont) errors.push('Select a first name font');
    if (!state.lastFont) errors.push('Select a last name font');

    var missingColors = [];
    if (!state.colors.main) missingColors.push('Main');
    if (!state.colors.outline1) missingColors.push('Outline 1');
    if (!state.colors.outline2) missingColors.push('Outline 2');
    if (missingColors.length > 0) {
      errors.push('Select colors: ' + missingColors.join(', '));
    }

    return errors;
  }

  // Switch to a mobile tab programmatically (no-op on desktop since all panels are visible)
  function switchToTab(tabName) {
    var tabBtn = document.querySelector('.ircd-builder-tab[data-tab="' + tabName + '"]');
    if (tabBtn) tabBtn.click();
  }

  // For printing: chrome, holo, and fluorescent material colors export as
  // white (#ffffff) because the vinyl itself provides the color/effect.
  function resolveExportColor(colorVal) {
    if (colorVal === 'chrome' || colorVal === 'holo') return '#ffffff';
    if (state.mediaType.indexOf('flo-') === 0) {
      var floHex = FLUORESCENT_COLORS[state.mediaType];
      if (colorVal === floHex) return '#ffffff';
    }
    return colorVal;
  }

  // ════════════════════════════════════════════════════════
  // PREVIEW CAPTURE (SVG → PNG for cart image)
  // ════════════════════════════════════════════════════════

  // When an SVG is loaded as an <img> for canvas capture, the browser
  // can't access page-level CSS fonts or external images. We need to
  // embed these resources directly into the SVG as base64 data URIs.

  var _googleFontsCss = null; // cached after first fetch

  // Fetch a URL and return its content as a base64 data URI
  function fetchAsDataUri(url, mimeType) {
    return fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('Fetch failed: ' + r.status);
        return r.arrayBuffer();
      })
      .then(function (buf) {
        var bytes = new Uint8Array(buf);
        var binary = '';
        for (var i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        return 'data:' + mimeType + ';base64,' + btoa(binary);
      });
  }

  // Fetch and cache the Google Fonts CSS from the page's <link> tag
  function getGoogleFontsCss() {
    if (_googleFontsCss) return Promise.resolve(_googleFontsCss);
    var link = document.querySelector('link[href*="fonts.googleapis.com"]');
    if (!link) return Promise.resolve('');
    return fetch(link.href)
      .then(function (r) { return r.text(); })
      .then(function (css) { _googleFontsCss = css; return css; });
  }

  // Find the woff2 URL for a Google Font from the cached CSS
  function resolveGoogleFontUrl(fontId) {
    return getGoogleFontsCss().then(function (css) {
      var blocks = css.split('@font-face');
      for (var i = 1; i < blocks.length; i++) {
        var block = blocks[i];
        if (block.indexOf("'" + fontId + "'") !== -1 ||
            block.indexOf('"' + fontId + '"') !== -1) {
          var urlMatch = block.match(/src:\s*url\(([^)]+)\)/);
          if (urlMatch) return urlMatch[1];
        }
      }
      return null;
    });
  }

  // Get a font's data as a base64 data URI (R2 custom or Google Font)
  function fetchFontDataUri(fontId) {
    for (var i = 0; i < state.fonts.length; i++) {
      if (state.fonts[i].id === fontId && state.fonts[i].source === 'r2') {
        var url = ASSETS_BASE + '/r2/fonts/' + encodeURIComponent(state.fonts[i].file);
        return fetchAsDataUri(url, 'font/woff2');
      }
    }
    // Google Font: resolve its woff2 URL from the CSS, then fetch binary
    return resolveGoogleFontUrl(fontId).then(function (url) {
      if (!url) throw new Error('Font URL not found: ' + fontId);
      return fetchAsDataUri(url, 'font/woff2');
    });
  }

  // Embed fonts as base64 @font-face rules inside an SVG element
  function embedFontsInSvg(svgEl) {
    var fontsNeeded = [];
    if (state.firstFont) fontsNeeded.push(state.firstFont);
    if (state.lastFont && state.lastFont !== state.firstFont) fontsNeeded.push(state.lastFont);
    if (fontsNeeded.length === 0) return Promise.resolve();

    var promises = fontsNeeded.map(function (fontId) {
      return fetchFontDataUri(fontId).then(function (dataUri) {
        return { id: fontId, dataUri: dataUri };
      }).catch(function (err) {
        console.warn('[IRCD Name] Failed to embed font:', fontId, err);
        return null;
      });
    });

    return Promise.all(promises).then(function (results) {
      var css = '';
      for (var i = 0; i < results.length; i++) {
        if (!results[i]) continue;
        css += '@font-face { font-family: "' + results[i].id + '"; ';
        css += 'src: url("' + results[i].dataUri + '") format("woff2"); }\n';
      }
      if (css) {
        var ns = 'http://www.w3.org/2000/svg';
        var styleEl = document.createElementNS(ns, 'style');
        styleEl.textContent = css;
        svgEl.insertBefore(styleEl, svgEl.firstChild);
      }
    });
  }

  // Convert external <image> hrefs to inline data URIs
  function embedImagesInSvg(svgEl) {
    var images = svgEl.querySelectorAll('image');
    var promises = [];
    for (var i = 0; i < images.length; i++) {
      (function (img) {
        var href = img.getAttribute('href') ||
                   img.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
        if (!href || href.indexOf('data:') === 0) return;
        // Guess MIME from extension
        var ext = href.split('?')[0].split('.').pop().toLowerCase();
        var mime = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
                     svg: 'image/svg+xml', gif: 'image/gif', webp: 'image/webp' }[ext] || 'image/png';
        promises.push(
          fetchAsDataUri(href, mime).then(function (dataUri) {
            img.setAttribute('href', dataUri);
            img.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
          }).catch(function (err) {
            console.warn('[IRCD Name] Failed to embed image:', href, err);
          })
        );
      })(images[i]);
    }
    return Promise.all(promises);
  }

  // Capture the current SVG preview as a PNG blob.
  // Clones the SVG and embeds fonts + images before rendering to canvas.
  function capturePreviewPng() {
    var host = q('preview-host');
    var svgEl = host ? host.querySelector('svg') : null;
    if (!svgEl) return Promise.reject(new Error('No preview SVG found'));

    // Clone so we don't modify the live DOM
    var svgClone = svgEl.cloneNode(true);

    return Promise.all([embedFontsInSvg(svgClone), embedImagesInSvg(svgClone)])
      .then(function () {
        return new Promise(function (resolve, reject) {
          var vb = svgClone.getAttribute('viewBox');
          var parts = vb ? vb.split(/[\s,]+/) : [];
          var svgW = parts.length === 4 ? parseFloat(parts[2]) : (parseFloat(svgClone.getAttribute('width')) || 800);
          var svgH = parts.length === 4 ? parseFloat(parts[3]) : (parseFloat(svgClone.getAttribute('height')) || 800);

          // Render at 2x for good quality, capped at 2000px wide
          var scale = Math.min(2, 2000 / svgW);
          var canvasW = Math.round(svgW * scale);
          var canvasH = Math.round(svgH * scale);

          var svgText = new XMLSerializer().serializeToString(svgClone);
          var blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
          var url = URL.createObjectURL(blob);

          var img = new Image();
          img.onload = function () {
            var canvas = document.createElement('canvas');
            canvas.width = canvasW;
            canvas.height = canvasH;
            var ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, canvasW, canvasH);
            URL.revokeObjectURL(url);
            canvas.toBlob(function (pngBlob) {
              if (pngBlob) resolve(pngBlob);
              else reject(new Error('Canvas toBlob failed'));
            }, 'image/png');
          };
          img.onerror = function () {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load SVG into Image'));
          };
          img.src = url;
        });
      });
  }

  // Upload a PNG blob to the worker and return the public URL
  function uploadPreviewPng(pngBlob) {
    var timestamp = Date.now();
    var rand = Math.random().toString(36).substring(2, 8);
    var filename = 'previews/cart-' + timestamp + '-' + rand + '.png';

    return fetch(ASSETS_BASE + '/r2/' + filename, {
      method: 'PUT',
      headers: { 'Content-Type': 'image/png' },
      body: pngBlob
    })
    .then(function (r) {
      if (!r.ok) throw new Error('Preview upload failed: ' + r.status);
      return r.json();
    })
    .then(function (data) {
      return data.url;
    });
  }


  function buildExport() {
    var pricePerSheet = PRICING[state.mediaType] || PRICING.standard;
    var namesPerSheet = state.lastLayout ? state.lastLayout.length : 0;

    return {
      version: 1,
      builderType: 'name',
      firstName: state.firstName.trim(),
      lastName: state.lastName.trim(),
      firstFont: state.firstFont,
      lastFont: state.lastFont,
      layout: state.layout,
      dateOrdered: formatMMDDYYYY(new Date()),
      mediaType: state.mediaType,
      scale: state.scale,
      sheetQuantity: state.sheetQuantity,
      namesPerSheet: namesPerSheet,
      pricing: {
        pricePerSheet: pricePerSheet,
        totalPrice: pricePerSheet * state.sheetQuantity
      },
      colors: {
        main: resolveExportColor(state.colors.main),
        outline1: resolveExportColor(state.colors.outline1),
        outline2: resolveExportColor(state.colors.outline2)
      },
      layout_positions: (state.lastLayout || []).map(function (p) {
        return { x: p.x, y: p.y, w: p.w, h: p.h };
      })
    };
  }

  // Helper: POST to /cart/add.js with the given properties
  function postToCart(variantId, config, properties) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: config.sheetQuantity,
        properties: properties
      })
    })
    .then(function (r) {
      if (!r.ok) throw new Error('Cart add failed: ' + r.status);
      return r.json();
    });
  }

  // Build the line item properties object for the cart
  function buildCartProperties(config, previewUrl) {
    var props = {
      '_ircd_name_config': JSON.stringify(config),
      'Builder': 'Name Builder',
      'First Name': config.firstName,
      'Last Name': config.lastName,
      'First Name Font': config.firstFont,
      'Last Name Font': config.lastFont,
      'Media Type': MEDIA_TYPE_NAMES[config.mediaType] || 'Standard',
      'Scale': config.scale,
      'Names Per Sheet': String(config.namesPerSheet)
    };
    if (previewUrl) {
      props['_ircd_name_preview'] = previewUrl;
      // Visible duplicate so the proof URL appears prominently in Shopify admin
      // and in default order confirmation emails (filtered from cart/order page displays).
      props['Proof Image'] = previewUrl;
    }
    return props;
  }

  function addToCart() {
    var errors = validateOrder();
    var msgEl = q('validation-msg');
    var cartBtn = q('add-to-cart');

    if (errors.length > 0) {
      if (msgEl) {
        msgEl.textContent = errors.join(' \u2022 ');
        msgEl.classList.add('is-error');
      }
      if (typeof IRCD !== 'undefined' && IRCD.toast) IRCD.toast(errors[0], 'error');

      // Auto-switch to the tab where the customer needs to take action
      var firstError = errors[0];
      if (firstError.indexOf('name') !== -1 || firstError.indexOf('font') !== -1) {
        switchToTab('info');
      } else if (firstError.indexOf('Select colors') !== -1) {
        switchToTab('colors');
      }
      return;
    }

    // Clear validation
    if (msgEl) { msgEl.textContent = ''; msgEl.classList.remove('is-error'); }

    // Build configuration
    var config = buildExport();
    console.log('[IRCD Name] Config JSON:', JSON.stringify(config, null, 2));
    var variantId = VARIANT_IDS[config.mediaType] || VARIANT_IDS.standard;

    // Check if variant IDs have been configured
    if (!variantId || variantId === 'REPLACE_WITH_VARIANT_ID') {
      if (msgEl) {
        msgEl.textContent = 'Product not yet configured. Please contact the store owner.';
        msgEl.classList.add('is-error');
      }
      return;
    }

    // Show loading state
    if (cartBtn) {
      cartBtn.disabled = true;
      cartBtn.classList.add('is-loading');
      cartBtn.textContent = 'Adding...';
    }

    // Capture preview image, then add to cart
    var capturedPreviewUrl = null;
    capturePreviewPng()
      .then(function (blob) { return uploadPreviewPng(blob); })
      .catch(function (err) {
        // If preview capture/upload fails, continue without it
        console.warn('[IRCD Name] Preview capture failed, adding to cart without preview:', err);
        return null;
      })
      .then(function (previewUrl) {
        capturedPreviewUrl = previewUrl;
        var properties = buildCartProperties(config, previewUrl);
        return postToCart(variantId, config, properties);
      })
      .then(function () {
        saveBuildToR2(config, capturedPreviewUrl);
        onCartSuccess(cartBtn);
      })
      .catch(function (err) { onCartError(err, cartBtn); });
  }

  function onCartSuccess(cartBtn) {
    if (typeof IRCD !== 'undefined' && IRCD.toast) {
      IRCD.toast('Added to cart!', 'success');
    }
    updateCartBadge();
    if (cartBtn) {
      cartBtn.classList.remove('is-loading');
      cartBtn.classList.add('is-success');
      cartBtn.textContent = 'Added!';
      setTimeout(function () {
        cartBtn.disabled = false;
        cartBtn.classList.remove('is-success');
        cartBtn.textContent = 'Add to Cart';
      }, 2000);
    }
  }

  function onCartError(err, cartBtn) {
    console.error('Cart add error:', err);
    if (typeof IRCD !== 'undefined' && IRCD.toast) {
      IRCD.toast('Failed to add to cart. Please try again.', 'error');
    }
    if (cartBtn) {
      cartBtn.disabled = false;
      cartBtn.classList.remove('is-loading');
      cartBtn.textContent = 'Add to Cart';
    }
  }

  function updateCartBadge() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var badges = document.querySelectorAll('[data-cart-count]');
        for (var i = 0; i < badges.length; i++) {
          badges[i].textContent = String(cart.item_count);
          badges[i].style.display = cart.item_count > 0 ? '' : 'none';
        }
      })
      .catch(function () { /* silent fail */ });
  }


  // ════════════════════════════════════════════════════════
  // HEALTH CHECK
  // ════════════════════════════════════════════════════════

  function healthCheck() {
    var dotEl = q('health-dot');
    var textEl = q('health-text');

    fetch(ASSETS_BASE + '/_health', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (j.ok) {
          if (dotEl) dotEl.className = 'ircd-health-dot is-ok';
          if (textEl) textEl.textContent = 'Healthy';
        } else {
          if (dotEl) dotEl.className = 'ircd-health-dot is-warning';
          if (textEl) textEl.textContent = 'Degraded';
        }
      })
      .catch(function () {
        if (dotEl) dotEl.className = 'ircd-health-dot';
        if (textEl) textEl.textContent = 'Offline';
      });
  }


  // ════════════════════════════════════════════════════════
  // EVENT BINDINGS
  // ════════════════════════════════════════════════════════

  function bindEvents() {
    // ── First & Last Name inputs ──
    var firstNameEl = q('first-name');
    var lastNameEl = q('last-name');

    if (firstNameEl) {
      firstNameEl.addEventListener('input', function () {
        state.firstName = firstNameEl.value;
        scheduleRender();
      });
    }

    if (lastNameEl) {
      lastNameEl.addEventListener('input', function () {
        state.lastName = lastNameEl.value;
        scheduleRender();
      });
    }

    // ── Font selectors (one per name) ──
    var firstFontEl = q('first-font');
    if (firstFontEl) {
      firstFontEl.addEventListener('change', function () {
        state.firstFont = firstFontEl.value;
        firstFontEl.style.fontFamily = '"' + firstFontEl.value + '", sans-serif';
        // Load custom font if needed, then update preview
        loadCustomFont(firstFontEl.value).then(function () {
          applyFontToInput('first');
          renderPreview();
        });
      });
    }

    var lastFontEl = q('last-font');
    if (lastFontEl) {
      lastFontEl.addEventListener('change', function () {
        state.lastFont = lastFontEl.value;
        lastFontEl.style.fontFamily = '"' + lastFontEl.value + '", sans-serif';
        // Load custom font if needed, then update preview
        loadCustomFont(lastFontEl.value).then(function () {
          applyFontToInput('last');
          renderPreview();
        });
      });
    }

    // ── Media Type buttons ──
    var mediaBtns = qa('media');
    for (var mi = 0; mi < mediaBtns.length; mi++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          state.mediaType = btn.getAttribute('data-media');
          for (var j = 0; j < mediaBtns.length; j++) {
            mediaBtns[j].classList.toggle('is-active', mediaBtns[j] === btn);
          }
          applyMediaTypeColorLogic(state.mediaType);
          renderAllPalettes();
          updatePricing();
          scheduleRender();
        });
      })(mediaBtns[mi]);
    }

    // ── Scale toggle ──
    var scaleBtns = qa('scale');
    for (var si = 0; si < scaleBtns.length; si++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          state.scale = btn.getAttribute('data-scale');
          for (var j = 0; j < scaleBtns.length; j++) {
            scaleBtns[j].classList.toggle('is-active', scaleBtns[j] === btn);
          }
          renderPreview();
        });
      })(scaleBtns[si]);
    }

    // ── Quantity stepper ──
    var qtyInput = q('qty-input');
    var qtyMinus = q('qty-minus');
    var qtyPlus = q('qty-plus');

    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        var v = parseInt(qtyInput.value, 10);
        if (isNaN(v) || v < 1) v = 1;
        if (v > 20) v = 20;
        qtyInput.value = v;
        state.sheetQuantity = v;
        updatePricing();
      });
    }

    if (qtyMinus) {
      qtyMinus.addEventListener('click', function () {
        if (state.sheetQuantity > 1) {
          state.sheetQuantity--;
          if (qtyInput) qtyInput.value = state.sheetQuantity;
          updatePricing();
        }
      });
    }

    if (qtyPlus) {
      qtyPlus.addEventListener('click', function () {
        if (state.sheetQuantity < 20) {
          state.sheetQuantity++;
          if (qtyInput) qtyInput.value = state.sheetQuantity;
          updatePricing();
        }
      });
    }

    // ── Add to Cart ──
    var cartBtn = q('add-to-cart');
    if (cartBtn) {
      cartBtn.addEventListener('click', addToCart);
    }

    // ── Load Previous Build button ──
    var loadBuildBtn = q('load-build-btn');
    if (loadBuildBtn) {
      loadBuildBtn.addEventListener('click', function () {
        var panel = q('builds-panel');
        if (!panel) return;
        var isOpen = panel.style.display !== 'none';
        if (isOpen) {
          panel.style.display = 'none';
          return;
        }
        panel.style.display = '';
        var listEl = q('builds-list');
        if (listEl) listEl.innerHTML = '<div style="text-align:center;padding:var(--space-4) 0;color:var(--color-text-tertiary);font-size:var(--text-sm);">Loading...</div>';
        fetchBuilds().then(function (builds) {
          renderBuildsList(builds);
        });
      });
    }

    // ── Preview refresh ──
    var refreshBtn = q('preview-refresh');
    if (refreshBtn) {
      refreshBtn.addEventListener('click', function () {
        renderPreview();
      });
    }

    // ── Mobile tab navigation ──
    var tabs = document.querySelectorAll('.ircd-builder-tab');
    for (var ti = 0; ti < tabs.length; ti++) {
      (function (tab) {
        tab.addEventListener('click', function () {
          var target = tab.getAttribute('data-tab');

          // Update tab active states + aria
          for (var j = 0; j < tabs.length; j++) {
            tabs[j].classList.toggle('is-active', tabs[j] === tab);
            tabs[j].setAttribute('aria-selected', tabs[j] === tab ? 'true' : 'false');
          }

          // Hide all columns and reset order state
          var cols = document.querySelectorAll('.ircd-builder__left, .ircd-builder__middle, .ircd-builder__right');
          for (var k = 0; k < cols.length; k++) {
            cols[k].classList.remove('is-active', 'is-order-active');
          }
          var orderWrapper = document.querySelector('.ircd-order-wrapper');
          if (orderWrapper) orderWrapper.classList.remove('is-active');

          if (target === 'order') {
            // Order tab: show right column in order-only mode
            var rightCol = document.querySelector('.ircd-builder__right');
            if (rightCol) rightCol.classList.add('is-order-active');
            if (orderWrapper) orderWrapper.classList.add('is-active');
          } else {
            // Other tabs: show the matching panel
            var targetPanel = document.querySelector('[data-panel="' + target + '"]');
            if (targetPanel) targetPanel.classList.add('is-active');
          }
        });
      })(tabs[ti]);
    }

    // Next/Back buttons simulate a tab click
    var nextBtns = document.querySelectorAll('[data-next-tab]');
    for (var ni = 0; ni < nextBtns.length; ni++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          var target = btn.getAttribute('data-next-tab');
          var targetTab = document.querySelector('.ircd-builder-tab[data-tab="' + target + '"]');
          if (targetTab) targetTab.click();
          window.scrollTo(0, 0);
        });
      })(nextBtns[ni]);
    }
  }


  // ════════════════════════════════════════════════════════
  // SAVE / LOAD PREVIOUS BUILDS (R2)
  // ════════════════════════════════════════════════════════

  // Save the build config to R2 after a successful cart add (fire-and-forget)
  function saveBuildToR2(config, previewUrl) {
    if (!CUSTOMER_EMAIL) return;

    var mediaLabel = MEDIA_TYPE_NAMES[config.mediaType] || 'Standard';
    var dateLabel = config.dateOrdered || formatMMDDYYYY(new Date());
    var name = (config.firstName + ' ' + config.lastName).trim() || 'Unnamed';
    var label = dateLabel + ' - ' + mediaLabel + ' - ' + name + ' x' + config.sheetQuantity;

    var buildData = {
      config: config,
      previewUrl: previewUrl || null,
      label: label,
      email: CUSTOMER_EMAIL,
      savedAt: new Date().toISOString()
    };

    var timestamp = Date.now();
    var key = 'name-builds/' + encodeURIComponent(CUSTOMER_EMAIL) + '/' + timestamp + '.json';

    fetch(ASSETS_BASE + '/r2/' + key, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildData)
    }).catch(function (err) {
      console.warn('[IRCD Name] Build save failed (non-critical):', err);
    });
  }

  // Fetch the list of previous builds for the logged-in customer
  function fetchBuilds() {
    if (!CUSTOMER_EMAIL) return Promise.resolve([]);
    return fetch(ASSETS_BASE + '/builds/list?type=name&email=' + encodeURIComponent(CUSTOMER_EMAIL))
      .then(function (r) {
        if (!r.ok) throw new Error('Build list fetch failed');
        return r.json();
      })
      .then(function (data) { return data.builds || []; })
      .catch(function (err) {
        console.warn('[IRCD Name] Failed to fetch builds:', err);
        return [];
      });
  }

  // Render the builds list in the panel
  function renderBuildsList(builds) {
    var listEl = q('builds-list');
    if (!listEl) return;

    if (builds.length === 0) {
      listEl.innerHTML = '<div style="text-align:center;padding:var(--space-4) 0;color:var(--color-text-tertiary);font-size:var(--text-sm);">No previous builds found.</div>';
      return;
    }

    listEl.innerHTML = '';
    for (var i = 0; i < builds.length; i++) {
      (function (build) {
        var item = document.createElement('div');
        item.className = 'ircd-build-item';

        var label = document.createElement('span');
        label.textContent = build.label;
        item.appendChild(label);

        if (build.previewUrl) {
          var tooltip = document.createElement('div');
          tooltip.className = 'ircd-build-preview-tooltip';
          var img = document.createElement('img');
          img.src = build.previewUrl;
          img.alt = 'Build preview';
          img.loading = 'lazy';
          tooltip.appendChild(img);
          item.appendChild(tooltip);

          item.addEventListener('mouseenter', function () {
            var panel = q('builds-panel');
            if (!panel) return;
            var panelRect = panel.getBoundingClientRect();
            tooltip.style.left = (panelRect.right + 8) + 'px';
            tooltip.style.top = panelRect.top + 'px';
          });
        }

        item.addEventListener('click', function () {
          loadBuildFromR2(build.key);
        });

        listEl.appendChild(item);
      })(builds[i]);
    }
  }

  // Load a specific build from R2 and apply it to the builder
  function loadBuildFromR2(key) {
    fetch(ASSETS_BASE + '/r2/' + key)
      .then(function (r) {
        if (!r.ok) throw new Error('Build fetch failed');
        return r.json();
      })
      .then(function (data) {
        if (!data.config) throw new Error('No config in build data');
        applyReorderConfig(data.config);
        if (typeof IRCD !== 'undefined' && IRCD.toast) IRCD.toast('Build loaded!', 'success');
        var panel = q('builds-panel');
        if (panel) panel.style.display = 'none';
      })
      .catch(function (err) {
        console.error('[IRCD Name] Failed to load build:', err);
        if (typeof IRCD !== 'undefined' && IRCD.toast) IRCD.toast('Failed to load build.', 'error');
      });
  }

  // Apply a saved config into state and sync the UI
  function applyReorderConfig(cfg) {
    if (!cfg) return;

    if (cfg.firstName != null) state.firstName = cfg.firstName;
    if (cfg.lastName != null) state.lastName = cfg.lastName;
    if (cfg.firstFont) state.firstFont = cfg.firstFont;
    if (cfg.lastFont) state.lastFont = cfg.lastFont;
    if (cfg.mediaType) state.mediaType = cfg.mediaType;
    if (cfg.scale) state.scale = cfg.scale;
    if (cfg.sheetQuantity) state.sheetQuantity = cfg.sheetQuantity;
    if (cfg.colors) {
      state.colors.main = cfg.colors.main || null;
      state.colors.outline1 = cfg.colors.outline1 || null;
      state.colors.outline2 = cfg.colors.outline2 || null;
    }

    // Sync DOM inputs and active buttons
    var firstNameInput = q('first-name');
    if (firstNameInput) firstNameInput.value = state.firstName;
    var lastNameInput = q('last-name');
    if (lastNameInput) lastNameInput.value = state.lastName;

    var firstFontSel = q('first-font');
    if (firstFontSel) firstFontSel.value = state.firstFont || '';
    var lastFontSel = q('last-font');
    if (lastFontSel) lastFontSel.value = state.lastFont || '';

    var mediaBtns = qa('media');
    for (var i = 0; i < mediaBtns.length; i++) {
      mediaBtns[i].classList.toggle('is-active',
        mediaBtns[i].getAttribute('data-media') === state.mediaType);
    }
    var scaleBtns = document.querySelectorAll('[data-scale]');
    for (var j = 0; j < scaleBtns.length; j++) {
      scaleBtns[j].classList.toggle('is-active',
        scaleBtns[j].getAttribute('data-scale') === state.scale);
    }
    var qtyInput = q('qty-input');
    if (qtyInput) qtyInput.value = state.sheetQuantity;

    renderAllPalettes();
    updatePricing();
    renderPreview();
  }


  // ════════════════════════════════════════════════════════
  // INITIALIZATION
  // ════════════════════════════════════════════════════════

  function init() {
    console.log('[IRCD Name Builder] Initializing...');

    bindEvents();
    healthCheck();
    renderAllPalettes();
    updatePricing();

    // Load fonts and template in parallel, then render preview
    Promise.all([loadFonts(), loadTemplate()])
      .then(function () {
        renderPreview();
        console.log('[IRCD Name Builder] Ready');
      })
      .catch(function (err) {
        console.error('[IRCD Name Builder] Init error:', err);
      });
  }

  // Wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
